import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  UserGroupIcon, 
  DocumentTextIcon, 
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import { patientsService, examsService, reportsService } from '../services/api';
import DashboardLayout from '../components/DashboardLayout';

const Dashboard = () => {
  const [stats, setStats] = useState([
    { label: 'Pacientes', value: '...', icon: UserGroupIcon, color: 'medical' },
    { label: 'Exames', value: '...', icon: DocumentTextIcon, color: 'health' },
    { label: 'Laudos', value: '...', icon: ChartBarIcon, color: 'tech' },
  ]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const [patients, exams, reports] = await Promise.all([
        patientsService.getAll().catch(() => []),
        examsService.getAll().catch(() => []),
        reportsService.getAll().catch(() => []),
      ]);

      setStats([
        { label: 'Pacientes', value: patients.length.toString(), icon: UserGroupIcon, color: 'medical' },
        { label: 'Exames', value: exams.length.toString(), icon: DocumentTextIcon, color: 'health' },
        { label: 'Laudos', value: reports.length.toString(), icon: ChartBarIcon, color: 'tech' },
      ]);
    } catch (err) {
      console.error('Erro ao carregar estatísticas:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">Dashboard</h1>
        <p className="text-gray-600">Bem-vindo à plataforma AudFast</p>
      </div>

        {/* Stats */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {stats.map((stat, index) => (
            <div key={index} className="card-gradient">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm font-medium mb-1">{stat.label}</p>
                  <p className="text-3xl font-bold text-gray-800">{stat.value}</p>
                </div>
                <div className={`w-12 h-12 bg-${stat.color}-100 rounded-lg flex items-center justify-center`}>
                  <stat.icon className={`w-6 h-6 text-${stat.color}-600`} />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Welcome Card */}
        <div className="bg-gradient-to-r from-medical-600 via-tech-600 to-health-600 rounded-xl shadow-lg p-8 text-white mb-8">
          <h2 className="text-2xl font-bold mb-4">🎉 Bem-vindo ao AudFast!</h2>
          <p className="text-lg mb-6 opacity-90">
            Comece cadastrando seus primeiros pacientes e realize exames audiométricos com análise inteligente de nexo causal.
          </p>
          <div className="flex space-x-4">
            <Link to="/patients/new" className="btn-outline">
              + Novo Paciente
            </Link>
            <button className="bg-white/20 hover:bg-white/30 px-6 py-3 rounded-lg font-semibold transition-colors">
              Ver Tutorial
            </button>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-2 gap-6">
          <div className="card">
            <h3 className="text-xl font-bold mb-4 text-gray-800">Ações Rápidas</h3>
            <div className="space-y-3">
              <Link to="/patients/new" className="block w-full text-left px-4 py-3 bg-medical-50 text-medical-600 rounded-lg font-medium hover:bg-medical-100 transition-colors">
                + Cadastrar Paciente
              </Link>
              <Link to="/report-wizard" className="block w-full text-left px-4 py-3 bg-health-50 text-health-600 rounded-lg font-medium hover:bg-health-100 transition-colors">
                + Novo Exame Audiométrico
              </Link>
              <Link to="/report-wizard" className="block w-full text-left px-4 py-3 bg-tech-50 text-tech-600 rounded-lg font-medium hover:bg-tech-100 transition-colors">
                + Organizar Laudo com IA
              </Link>
            </div>
          </div>

          <div className="card">
            <h3 className="text-xl font-bold mb-4 text-gray-800">Recursos Premium</h3>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-medical-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-medical-600 font-bold">✓</span>
                </div>
                <div>
                  <p className="font-semibold text-gray-800">Análise Inteligente de Nexo Ocupacional</p>
                  <p className="text-sm text-gray-600">IA correlaciona dados e identifica padrões de exposição</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-health-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-health-600 font-bold">✓</span>
                </div>
                <div>
                  <p className="font-semibold text-gray-800">Conformidade NR-7</p>
                  <p className="text-sm text-gray-600">Critérios da Portaria 19/98 aplicados automaticamente</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-tech-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-tech-600 font-bold">✓</span>
                </div>
                <div>
                  <p className="font-semibold text-gray-800">Histórico Longitudinal</p>
                  <p className="text-sm text-gray-600">Acompanhamento completo de evolução audiométrica</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </DashboardLayout>
  );
};

export default Dashboard;
